import React, { useEffect } from 'react';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import CalendarEditIcon from 'mdi-material-ui/CalendarEdit';

import Button from 'components/Button/Button';

import DialogHeader from 'components/DialogHeader/DialogHeader';

import { FormHelperText, Grid, MenuItem, TextField } from '@material-ui/core';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import { AllowancesUnitCount } from 'components/ClockOutDialog/AllowanceDialog/AllowancesUnitCount/AllowancesUnitCount';
import DatePickerTypeable from 'components/DatePickerTypeable/DatePickerTypeable';
import TextInput from 'components/TextInput/TextInput';
import dayjs from 'dayjs';
import { sortBy } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectEmployerAllowancesByBusinessId } from 'store/employerAllowances/selectors/employerAllowances.selectors';
import { addRecordAllowance } from 'store/recordAllowances/thunks/addRecordAllowance.thunk';
import {
  selectRecordEditPending,
  selectRecordEditSelectedResource,
} from 'store/records/selectors/records.selectors';
import { createAllowanceRecord } from 'store/records/thunks/createAllowanceRecord.thunk';
import { returnIsFirefoxOrSafari } from 'utils/returnIsFirefoxOrSafari.util';
import useStyles from '../../RecordDialogLayoutStyles';

export const AddRecordAllowanceDialog = ({
  parentClockIn,
  parentClockOut,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isFirefoxOrSafari = returnIsFirefoxOrSafari();

  const dispatch = useDispatch();

  const [selectedAllowanceType, setSelectedAllowanceType] = useState('RIDE');

  const [unitCount, setUnitCount] = useState('');

  const [allowanceRecordAttributes, setAllowanceRecordAttributes] = useState({
    clockInAt: undefined,
    clockOutAt: undefined,
  });
  const dateFormat = 'YYYY-MM-DDTHH:mm';

  const selectedRecordEditResource = useSelector(
    selectRecordEditSelectedResource
  );

  const resourceEmployerBusinessId =
    selectedRecordEditResource?.employerBusinessId;

  const calculateAllowanceRecordDefaultTimes = (
    parentClockIn,
    parentClockOut
  ) => {
    const parentClockInDate = dayjs(parentClockIn);
    const parentClockOutDate = dayjs(parentClockOut);

    const parentDiff = parentClockOutDate.diff(parentClockInDate, 'minutes');

    let allowanceLengthMinutes = 15;

    if (parentDiff < allowanceLengthMinutes) {
      allowanceLengthMinutes = parentDiff;
    }

    const defaultClockIn = parentClockInDate.format(dateFormat);
    const defaultClockOut = parentClockInDate
      .add(allowanceLengthMinutes, 'minutes')
      .format(dateFormat);

    setAllowanceRecordAttributes((prev) => {
      return {
        ...prev,
        clockInAt: defaultClockIn,
        clockOutAt: defaultClockOut,
      };
    });
  };

  useEffect(() => {
    calculateAllowanceRecordDefaultTimes(parentClockIn, parentClockOut);
  }, [parentClockIn, parentClockOut]);

  const setClockedTimes = (attributeName, value) => {
    const valueFormated = dayjs(value).format(dateFormat);
    setAllowanceRecordAttributes((prev) => {
      return { ...prev, [attributeName]: valueFormated };
    });
  };

  const timeDiff = dayjs(allowanceRecordAttributes.clockOutAt).diff(
    dayjs(allowanceRecordAttributes.clockInAt),
    'minutes'
  );

  const handleClockInGreaterThanClockout = (clockInAt, clockOutAt) => {
    const clockIn = dayjs(clockInAt).format(dateFormat);
    const clockOut = dayjs(clockOutAt).format(dateFormat);

    if (clockIn > clockOut) {
      setAllowanceRecordAttributes((prev) => {
        return { ...prev, clockInAt: clockIn, clockOutAt: clockIn };
      });
    }
  };

  const onSave = async () => {
    if (selectedAllowanceType === 'RIDE') {
      const allowanceRecord = {
        clockInAt: dayjs(allowanceRecordAttributes.clockInAt).toISOString(),
        clockOutAt: dayjs(allowanceRecordAttributes.clockOutAt).toISOString(),
        type: selectedAllowanceType,
      };
      dispatch(createAllowanceRecord(allowanceRecord));
    } else {
      dispatch(
        addRecordAllowance({ unitCount, allowanceType: selectedAllowanceType })
      );
    }

    onClose();
  };

  const recordEditPending = useSelector(selectRecordEditPending);

  const allowanceClockInBeforeParentClockIn =
    allowanceRecordAttributes.clockInAt < parentClockIn;
  const allowanceClockOutAfterParentClockOut =
    allowanceRecordAttributes.clockOutAt > parentClockOut;

  const handleChange = (value) => {
    setSelectedAllowanceType(value);
  };

  const employerAllowances = useSelector((state) =>
    selectEmployerAllowancesByBusinessId(state, resourceEmployerBusinessId)
  );

  const recordAllowanceTypeSalaries = Object.values(
    DEFAULT_ADDITIONAL_SALARY_TYPES
  )
    .filter(
      (additionalSalary) => additionalSalary.baseType === 'RECORD_ALLOWANCE'
    )
    .map((salary) => salary.salaryType);

  const recordAllowanceTypeOptions = employerAllowances.filter((allowance) =>
    recordAllowanceTypeSalaries.includes(allowance.salaryType)
  );

  const recordAllowanceTypeOptionsWithName = sortBy(
    [...recordAllowanceTypeOptions, { salaryType: 'RIDE' }].map(
      (allowance) => ({
        ...allowance,
        name: t(`report.salaries.${allowance.salaryType.toLowerCase()}`),
      })
    ),
    'name'
  );

  return (
    <Dialog
      classes={{ paper: classes.dialogPaper }}
      open={true}
      onClose={() => onClose()}
      aria-labelledby="add-allowance-dialog"
      maxWidth="sm"
    >
      <DialogHeader
        icon={<CalendarEditIcon />}
        onClose={() => onClose()}
        title={t('calendar.add_allowance')}
      />
      <DialogContent>
        <Grid container>
          <Grid
            item
            xs={12}
            style={{ paddingTop: '8px', paddingBottom: '8px' }}
          >
            <TextInput
              displayEmpty
              fullWidth
              id="edit-record-type-select"
              label={t('calendar.type')}
              onChange={(e) => handleChange(e.target.value)}
              select
              shrink
              value={selectedAllowanceType}
            >
              {recordAllowanceTypeOptionsWithName.map((item) => (
                <MenuItem key={item.salaryType} value={item.salaryType}>
                  {item.name}
                </MenuItem>
              ))}
            </TextInput>
          </Grid>

          {selectedAllowanceType === 'RIDE' ? (
            <Grid container spacing={1}>
              <Grid item xs={5}>
                {isFirefoxOrSafari ? (
                  <DatePickerTypeable
                    label={t('calendar.start_date')}
                    iconVisible
                    showTimeSelect
                    timeIntervals={5}
                    dateFormat="dd.MM.yyyy HH:mm"
                    value={allowanceRecordAttributes.clockInAt}
                    onChange={(date) => setClockedTimes('clockInAt', date)}
                  />
                ) : (
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ className: classes.textInput }}
                    fullWidth
                    label={t('calendar.start_date')}
                    variant="outlined"
                    size="small"
                    type="datetime-local"
                    value={allowanceRecordAttributes.clockInAt}
                    onChange={(event) =>
                      setClockedTimes('clockInAt', event.target.value)
                    }
                    onBlur={(event) =>
                      handleClockInGreaterThanClockout(
                        event.target.value,
                        allowanceRecordAttributes.clockOutAt
                      )
                    }
                  />
                )}
              </Grid>
              <Grid item xs={5}>
                {isFirefoxOrSafari ? (
                  <DatePickerTypeable
                    label={t('calendar.end_date')}
                    iconVisible
                    showTimeSelect
                    timeIntervals={5}
                    dateFormat="dd.MM.yyyy HH:mm"
                    value={allowanceRecordAttributes.clockOutAt}
                    onChange={(date) => setClockedTimes('clockOutAt', date)}
                    minDate={new Date(allowanceRecordAttributes.clockInAt)}
                    minTime={new Date(allowanceRecordAttributes.clockInAt)}
                    maxTime={new Date().setHours(23, 59, 59, 999)}
                  />
                ) : (
                  <TextField
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ className: classes.textInput }}
                    fullWidth
                    label={t('calendar.end_date')}
                    variant="outlined"
                    size="small"
                    type="datetime-local"
                    value={allowanceRecordAttributes.clockOutAt}
                    onChange={(event) =>
                      setClockedTimes('clockOutAt', event.target.value)
                    }
                    onBlur={(event) =>
                      handleClockInGreaterThanClockout(
                        allowanceRecordAttributes.clockInAt,
                        event.target.value
                      )
                    }
                  />
                )}
              </Grid>
              <Grid item xs={2} style={{ margin: 'auto' }}>
                <p className={classes.allowanceTimeDiff}>{`${timeDiff} min`}</p>
              </Grid>
              {allowanceClockInBeforeParentClockIn ||
              allowanceClockOutAfterParentClockOut ? (
                <Grid item xs={12} className={classes.centerItems}>
                  <FormHelperText className={classes.warningText}>
                    {t('calendar.allowance_is_beyond_record_timeframe')}
                  </FormHelperText>
                </Grid>
              ) : null}
            </Grid>
          ) : (
            <AllowancesUnitCount
              t={t}
              unit={
                recordAllowanceTypeOptions.find(
                  (typeOption) =>
                    typeOption.salaryType === selectedAllowanceType
                )?.unit
              }
              unitCount={unitCount}
              setUnitCount={setUnitCount}
            ></AllowancesUnitCount>
          )}
        </Grid>
      </DialogContent>
      <Divider />
      <DialogActions className={classes.dialogActions}>
        <Button onClick={() => onClose()} color="primary">
          {t('common.cancel')}
        </Button>
        <Button
          disabled={
            recordEditPending ||
            (selectedAllowanceType !== 'RIDE' && !unitCount)
          }
          onClick={onSave}
          color="primary"
        >
          {!recordEditPending ? t('common.save') : <ButtonLoading />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
